import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FilterPanelEditorConfigurationModel } from 'src/ca-shared/filter-panel/models/filter-panel-editor-configuration.model';
import { FilterPanelEditorComponent } from 'src/ca-shared/filter-panel/models/filter-panel-editor.component';
import { FormDto } from 'src/core/models/quality-management/form.dto';
import { FormVersionSelectionModel } from 'src/core/models/quality-management/form-version-selection.model';
import { FormVersionSelectionConfigurationModel } from '../models/form-version-selection-configuration.model';

@Component({
  selector: 'ca-form-version-multi-selection-panel',
  templateUrl: './form-version-multi-selection-panel.component.html',
  styleUrls: ['./form-version-multi-selection-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormVersionMultiSelectionPanelComponent
  extends FilterPanelEditorComponent
  implements OnDestroy
{
  actualConfig: FormVersionSelectionConfigurationModel;
  forms: FormDto[] = [];

  private _value: FormVersionSelectionModel[] | null = null;
  private readonly destroy$ = new Subject<void>();

  set config(value: FilterPanelEditorConfigurationModel) {
    this.actualConfig = value as FormVersionSelectionConfigurationModel;
  }

  get config(): FilterPanelEditorConfigurationModel {
    return this.actualConfig;
  }

  get value(): FormVersionSelectionModel[] | null {
    return this._value;
  }

  // Non-array values (e.g. the single-version shape restored from another tab) are treated as "all latest".
  set value(value: any) {
    const selections = Array.isArray(value) ? value : null;

    this.editorForm.patchValue({ editor: selections });
    this._value = selections;
  }

  constructor(
    protected fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {
    super(fb);

    this.editorForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this._value = Array.isArray(val.editor) ? val.editor : null;
      this.onChange();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  autoSelectFirstItem(value: boolean | null): void {}

  reload(): void {
    this.forms = Array.isArray(this.extraData?.forms) ? [...this.extraData.forms] : [];

    // Keep the value in step with the selected forms until the selector re-emits.
    if (Array.isArray(this._value)) {
      const formIds = new Set(this.forms.map(form => form?.id));
      this._value = this._value.filter(selection => formIds.has(selection?.formId));
    }

    this.cdr.markForCheck();
    this.parentFilterPanel?.notifyReloadFinished(this.config.key);
  }

  // Clear All suppresses the form editor's onChange, so drop the forms here as well.
  reset(): void {
    this.forms = [];
    this.value = null;
    this.cdr.markForCheck();
  }

  init(): void {
    super.init();

    if (this.actualConfig.autoLoad) {
      this.reload();
    }
  }
}
